import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Avatar, Card, CardContent, CardHeader, Grid, IconButton, Tooltip} from '@mui/material';
import Button from '@mui/material/Button';
import {AddOutlined, Check, Clear, Close, PlaylistAddCheck} from '@mui/icons-material';
import {useAppDispatch} from '../store';
import {useAppTranslation} from '../services/i18n';
import {getFilteredTotal, isApiResultError, type PagedResult} from '../helpers/api';
import {dateToGui, toCalendarDate} from '../helpers/date';
import {decimalNumber} from '../helpers/number';
import {fetchApprovalRequests} from 'store/approvals';
import {
    APPROVAL_PENDING_STATUS,
    approvalMissingFields,
    approvalSubjectLabel,
    ApprovalRequestInfo,
    type ApprovalRequestStatus,
    type ApprovalRequestType,
    isApprovable,
    isApprovalEditable,
    isApprovalPending,
    toApprovalRequestInfo
} from 'model/approval';
import {
    colFilterAny,
    colNoSort,
    createCol,
    DataGrid,
    DataGridCol,
    DataGridColFilter,
    DataGridItemAction,
    DataGridMode,
    DataGridState,
    FilterContext,
    OrderDirType,
    ItemsState
} from 'components/DataGrid';
import {useDataGridSelection} from 'components/DataGridSelection';
import {getWindowState, setWindowState} from 'utils/utils';
import {userInitials} from 'components/form/UserSelectFormField';
import {TextFormField} from 'components/form/TextFormField';
import {Tag} from 'components/Tag';
import {ApprovalStatusTag} from 'components/approval/ApprovalStatusTag';
import {ApprovalApproveDialog} from 'components/approval/ApprovalApproveDialog';
import {ApprovalCreateTypeDialog} from 'components/approval/ApprovalCreateTypeDialog';
import {ApprovalRejectDialog} from 'components/approval/ApprovalRejectDialog';
import {ApprovalFilterDialog} from 'components/approval/ApprovalFilterDialog';

export type ApprovalFilterType = 'filterUserIds' | 'provider' | 'filterRequestTypes' | 'filterStatuses';

export interface ApprovalGridFilter {
    search?: string;
    filterUserIds?: number[];
    /** single free-text value; the API takes an array of provider names */
    provider?: string;
    filterRequestTypes?: ApprovalRequestType[];
    filterStatuses?: ApprovalRequestStatus[];
}

/** "Select all" re-queries the whole cohort in chunks, as the AngularJS model does. */
const SELECT_ALL_PAGE_SIZE = 200;

/** `Pageable::MAX_PAGE_SIZE` -- a larger `per_page` is refused with a 400. */
const MAX_PAGE_SIZE = 1000;

/** what the queue opens on -- see `emptyState` for what "clear filters" returns to */
const defaultState: DataGridState<ApprovalRequestInfo, ApprovalGridFilter> = {
    orderCol: 'created_at',
    orderDir: OrderDirType.DESC,
    filter: {
        search: '',
        filterUserIds: [],
        provider: '',
        filterRequestTypes: [],
        // the queue opens on what still waits for a decision; the rest stays reachable
        filterStatuses: [APPROVAL_PENDING_STATUS],
    },
    pageSize: 25,
    page: 1
};

/**
 * The unfiltered state. The grid gets this as its default, so the pre-filled status
 * counts as a filter the user can drop in one click instead of an invisible baseline.
 */
const emptyState: DataGridState<ApprovalRequestInfo, ApprovalGridFilter> = {
    ...defaultState,
    filter: {...defaultState.filter, filterStatuses: []}
};

const WINDOW_STATE = 'approvals';

/**
 * Filters, search and sorting survive a trip to the detail and back -- the same
 * window-scoped state the content manager and the study plans keep.
 */
const restoreState = (): DataGridState<ApprovalRequestInfo, ApprovalGridFilter> => {
    const stored = getWindowState(WINDOW_STATE, 'state', defaultState);
    // paging is deliberately not restored: further pages are appended to the loaded
    // ones, so re-entering on page 3 would render that chunk alone
    return {...stored, page: 1, pageSize: defaultState.pageSize};
};

const getRowId = (item: ApprovalRequestInfo) => item.id;

// solid green approve, light red reject -- the decision pair drawn in the design
const approveButtonStyle = {
    borderRadius: '6px',
    marginRight: '4px',
    background: 'var(--color-primary)',
    // the space can recolour primary, so take the contrast colour with it
    color: 'var(--color-primary-contrast)',
    '&:hover': {background: 'var(--color-primary60)'},
    '&.Mui-disabled': {background: 'rgba(0, 0, 0, 0.06)', color: 'rgba(0, 0, 0, 0.26)'},
};

const rejectButtonStyle = {
    borderRadius: '6px',
    background: 'var(--color-error-back)',
    color: '#A32409',
    '&:hover': {background: '#FBD5CE'},
};

export const ApprovalsPage = () => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();

    const [dataGridState, setDataGridState] = useState(restoreState);
    const [itemsState, setItemsState] = useState<ItemsState<ApprovalRequestInfo>>({items: [], isLoading: true});
    const [colFilter, setColFilter] = useState<DataGridColFilter<ApprovalGridFilter, ApprovalFilterType>>();
    const [approveItems, setApproveItems] = useState<ApprovalRequestInfo[]>();
    const [isBulkApprove, setIsBulkApprove] = useState(false);
    const [rejectItem, setRejectItem] = useState<ApprovalRequestInfo>();
    const [refreshKey, setRefreshKey] = useState(0);
    const [isCreateOpen, setIsCreateOpen] = useState(false);

    const {filter, orderCol, orderDir, page, pageSize} = dataGridState;

    const queryArgs = useMemo(() => ({
        filterFulltext: filter.search || undefined,
        filterUserIds: filter.filterUserIds?.length ? filter.filterUserIds : undefined,
        filterProviders: filter.provider ? [filter.provider] : undefined,
        filterRequestTypes: filter.filterRequestTypes?.length ? filter.filterRequestTypes : undefined,
        filterStatuses: filter.filterStatuses?.length ? filter.filterStatuses : undefined,
        sort: orderCol ? String(orderCol) + ':' + (orderDir || 'asc') : undefined,
    }), [filter, orderCol, orderDir]);

    const handleFetch = useCallback(async () => {
        setItemsState(s => ({...s, isLoading: true}));
        const res = await dispatch(fetchApprovalRequests({...queryArgs, page, perPage: pageSize}));
        if (isApiResultError(res)) {
            setItemsState(s => ({...s, isLoading: false}));
            return;
        }
        const result = res.payload as PagedResult<any>;
        const items = (result.items || []).map(toApprovalRequestInfo);
        setItemsState(s => ({
            items: page && page > 1 ? [...(s.items || []), ...items] : items,
            total: getFilteredTotal(result),
            isLoading: false
        }));
    }, [queryArgs, page, pageSize, dispatch]);

    useEffect(() => {
        handleFetch().then();
    }, [handleFetch, refreshKey]);

    useEffect(() => {
        setWindowState(WINDOW_STATE, 'state', dataGridState);
    }, [dataGridState]);

    /**
     * Reloads from the first page, but wide enough to cover everything the user
     * had already loaded -- otherwise deciding a request near the bottom would
     * collapse the list back to the first page.
     */
    const handleRefresh = useCallback(() => {
        setDataGridState(s => ({
            ...s,
            page: 1,
            pageSize: Math.min(MAX_PAGE_SIZE,
                Math.max(defaultState.pageSize || 0, itemsState.items.length))
        }));
        setRefreshKey(k => k + 1);
    }, [itemsState.items.length]);

    /**
     * Backs the header checkbox: walks every page the current filter matches, so
     * the selection covers the whole cohort rather than the rows already loaded --
     * same semantics as SelectableModel in the AngularJS user management.
     */
    const handleFetchAll = useCallback(async () => {
        const all: ApprovalRequestInfo[] = [];
        let currentPage = 1;
        let pageCount = 1;
        do {
            const res = await dispatch(fetchApprovalRequests({
                ...queryArgs, page: currentPage, perPage: SELECT_ALL_PAGE_SIZE
            }));
            if (isApiResultError(res)) {
                // a half-loaded cohort must not pass as the whole one
                throw new Error('approval select all: page ' + currentPage + ' failed');
            }
            const result = res.payload as PagedResult<any>;
            all.push(...(result.items || []).map(toApprovalRequestInfo));
            pageCount = result.pageCount || 1;
            currentPage++;
        } while (currentPage <= pageCount);
        return all;
    }, [queryArgs, dispatch]);

    const renderLead = useCallback((item: ApprovalRequestInfo) => (
        <Avatar sx={{width: 32, height: 32, fontSize: 12, background: 'var(--color-primary-back, #eee)', color: 'inherit'}}>
            {userInitials(item.user_name)}
        </Avatar>
    ), []);

    // changing the search or any filter drops the selection
    const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

    const selection = useDataGridSelection<ApprovalRequestInfo>({
        getId: getRowId,
        isSelectable: isApprovable,
        fetchAll: handleFetchAll,
        resetOn: filterKey,
        renderLead,
    });

    const {deselectAll} = selection;

    const handleDecided = useCallback(() => {
        deselectAll();
        handleRefresh();
    }, [deselectAll, handleRefresh]);

    const handleOpenDetail = useCallback((item: ApprovalRequestInfo) => {
        window.location.href = '#/approvals/' + item.id;
    }, []);

    const handleEdit = useCallback((item: ApprovalRequestInfo) => {
        window.location.href = '#/approvals/' + item.id + '/edit';
    }, []);

    const actions: DataGridItemAction<ApprovalRequestInfo>[] = useMemo(() => [
        {title: 'approvals.action.detail', callback: handleOpenDetail},
        {title: 'approvals.action.edit', callback: handleEdit, isApplicable: isApprovalEditable},
    ], [handleOpenDetail, handleEdit]);

    const rowActions = useCallback((item: ApprovalRequestInfo) => {
        if (!isApprovalPending(item)) {
            return null;
        }
        // rejection has no realization requirement, approval does
        const approvable = isApprovable(item);
        // the list omits the competences, so a row that has everything else names
        // the competence by deduction -- see approvalMissingFields
        const missing = approvable ? [] : approvalMissingFields(item);
        return <>
            <Tooltip title={approvable
                ? t('approvals.action.approve')
                : (missing.length
                    ? t('approvals.hint.missingFields', {fields: missing.map(key => t(key)).join(', ')})
                    : t('approvals.action.approveIncomplete'))}>
                {/* the span keeps the tooltip alive while the button is disabled */}
                <span>
                    <IconButton size={'small'} disabled={!approvable} sx={approveButtonStyle}
                        onClick={() => {
                            setIsBulkApprove(false);
                            setApproveItems([item]);
                        }}><Check fontSize={'small'}/></IconButton>
                </span>
            </Tooltip>
            <Tooltip title={t('approvals.action.reject')}>
                <IconButton size={'small'} sx={rejectButtonStyle}
                    onClick={() => setRejectItem(item)}><Clear fontSize={'small'}/></IconButton>
            </Tooltip>
        </>;
    }, [t]);

    const cols = useMemo(() => {
        const filterContext: FilterContext<ApprovalGridFilter, ApprovalFilterType> = {
            defaultFilter: emptyState.filter,
            currentFilter: dataGridState.filter,
            onOpen: setColFilter,
            onApply: (values) => {
                setDataGridState(s => ({...s, filter: {...s.filter, ...values}, page: 1}));
            }
        };
        const namedFilter = (name: ApprovalFilterType): DataGridColFilter<ApprovalGridFilter, ApprovalFilterType> => ({
            ...filterContext,
            filterName: name,
            // marked whenever the column narrows the list at all -- the queue opens
            // with a status filter already set, and that has to be visible too
            isApplied: () => {
                const value = dataGridState.filter[name];
                return Array.isArray(value) ? value.length > 0 : !!value;
            }
        });

        const cols: DataGridCol<ApprovalRequestInfo>[] = [
            colFilterAny(
                createCol(t('approvals.table.user'), 'user_name', (v) => v || '-', '16%'),
                namedFilter('filterUserIds')),
            // request_type, subject_provider and status are not in the API's sort whitelist
            colFilterAny(
                colNoSort(createCol(t('approvals.table.type'), 'request_type',
                    (v) => <Tag>{t('approvals.type.' + v)}</Tag>, '12%', 'sm')),
                namedFilter('filterRequestTypes')),
            createCol(t('approvals.table.subjectTitle'), 'subject_title',
                (v, item) => approvalSubjectLabel(item) || '-', '18%'),
            colFilterAny(
                colNoSort(createCol(t('approvals.table.provider'), 'subject_provider', (v) => v || '-', '13%', 'sm')),
                namedFilter('provider')),
            createCol(t('approvals.table.price'), 'price',
                (v) => (v || v === 0) ? decimalNumber(v, 0) : '-', '9%', 'md'),
            createCol(t('approvals.table.subjectDate'), 'subject_date', (v) => dateToGui(toCalendarDate(v)) || '-', '10%', 'md'),
            createCol(t('approvals.table.createdAt'), 'created_at', (v) => dateToGui(v) || '-', '10%', 'lg'),
            colFilterAny(
                colNoSort(createCol(t('approvals.table.status'), 'status',
                    (v) => <ApprovalStatusTag status={v}/>, '12%')),
                namedFilter('filterStatuses')),
        ];
        return cols;
    }, [t, dataGridState.filter]);

    const createFilter = useCallback((formProps: any) => <Grid container spacing={2}>
        <Grid size={{lg: 4, md: 6, xs: 12}}>
            <TextFormField name={'search'} type={'search'}
                label={t('approvals.filter.search')}
                onBlur={formProps.submitForm}
                maxlength={100}/>
        </Grid>
    </Grid>, [t]);

    const isSelecting = selection.count > 0;

    return <div className={'layout-fill layout-column tw-absolute'}>
        <div className={'kws-content-margin flex tw-h-100'}>
            <Card className={'tw-h-full tw-flex tw-flex-col'}>
                {isSelecting
                    ? <CardHeader
                        className={'max-sm:tw-flex-wrap max-sm:tw-gap-12'}
                        title={<>
                            <IconButton onClick={deselectAll} color={'inherit'}
                                aria-label={t('approvals.bulk.deselect')}><Close/></IconButton>
                            <span>{t('approvals.bulk.selected', {count: selection.count})}</span>
                        </>}
                        action={<Button color={'primary'} variant={'contained'} className={'tw-w-auto'}
                            onClick={() => {
                                setIsBulkApprove(true);
                                setApproveItems(selection.selectedItems);
                            }}>
                            {t('approvals.bulk.approve')}
                        </Button>}
                    />
                    : <CardHeader
                        className={'max-sm:tw-flex-wrap max-sm:tw-gap-12'}
                        title={<>
                            <PlaylistAddCheck/>
                            <span>{t('approvals.title')}</span>
                        </>}
                        action={<Button color={'primary'} variant={'contained'} className={'tw-w-auto'}
                            onClick={() => setIsCreateOpen(true)}>
                            <AddOutlined/>
                            {t('approvals.action.add')}
                        </Button>}
                    />}
                <CardContent className={'tw-p-32 tw-pb-0 tw-flex-grow tw-flex tw-flex-col tw-overflow-auto'}>
                    <DataGrid
                        cols={cols}
                        state={dataGridState}
                        defaultState={emptyState}
                        setState={setDataGridState}
                        itemsState={itemsState}
                        mode={DataGridMode.SERVER}
                        createFilter={createFilter}
                        actions={actions}
                        isActionMenu
                        selection={selection}
                        rowActions={rowActions}
                        rowClick={handleOpenDetail}
                        emptyListMessage={t('approvals.emptyList')}
                        emptySearchMessage={t('approvals.emptySearch')}
                    />
                    {!!colFilter && <ApprovalFilterDialog colFilter={colFilter} onClose={() => setColFilter(undefined)}/>}
                    {!!approveItems?.length && <ApprovalApproveDialog items={approveItems} isBulk={isBulkApprove}
                        onSuccess={handleDecided} onClose={() => setApproveItems(undefined)}/>}
                    {!!rejectItem && <ApprovalRejectDialog item={rejectItem}
                        onSuccess={handleDecided} onClose={() => setRejectItem(undefined)}/>}
                    {isCreateOpen && <ApprovalCreateTypeDialog onClose={() => setIsCreateOpen(false)}/>}
                </CardContent>
            </Card>
        </div>
    </div>;
};
